import { useState, useCallback } from 'react'
import { router } from '@inertiajs/react'
import type { AnalyticsDateRange, AnalyticsPeriod } from '@/types/analytics'

function formatDate(date: Date): string {
  return date.toISOString().slice(0, 10)
}

function computeDates(range: AnalyticsDateRange, customFrom: string, customTo: string): { from: string; to: string } {
  const today = new Date()
  switch (range) {
    case 'today':
      return { from: formatDate(today), to: formatDate(today) }
    case '7d': {
      const d = new Date(today)
      d.setDate(d.getDate() - 6)
      return { from: formatDate(d), to: formatDate(today) }
    }
    case '30d': {
      const d = new Date(today)
      d.setDate(d.getDate() - 29)
      return { from: formatDate(d), to: formatDate(today) }
    }
    case '90d': {
      const d = new Date(today)
      d.setDate(d.getDate() - 89)
      return { from: formatDate(d), to: formatDate(today) }
    }
    case 'custom':
      return { from: customFrom, to: customTo }
  }
}

function rangeFromPeriod(period: AnalyticsPeriod): AnalyticsDateRange {
  const today = formatDate(new Date())
  const { from, to } = period

  if (from === to && to === today) return 'today'

  const diffDays = Math.round(
    (new Date(to).getTime() - new Date(from).getTime()) / (1000 * 60 * 60 * 24)
  )

  if (to === today) {
    if (diffDays === 6) return '7d'
    if (diffDays === 29) return '30d'
    if (diffDays === 89) return '90d'
  }

  return 'custom'
}

export function useAnalyticsVM(period: AnalyticsPeriod) {
  const initialRange = rangeFromPeriod(period)
  const [activeRange, setActiveRange] = useState<AnalyticsDateRange>(initialRange)
  const [customFrom, setCustomFrom] = useState(period.from)
  const [customTo, setCustomTo] = useState(period.to)

  const navigate = useCallback(
    (from: string, to: string) => {
      router.get('/admin/analytics', { from, to }, { preserveState: true, preserveScroll: true })
    },
    []
  )

  const onRangeChange = useCallback(
    (range: AnalyticsDateRange) => {
      setActiveRange(range)
      if (range !== 'custom') {
        const { from, to } = computeDates(range, customFrom, customTo)
        navigate(from, to)
      }
    },
    [customFrom, customTo, navigate]
  )

  const onCustomFromChange = useCallback(
    (date: string) => {
      setCustomFrom(date)
      if (date && customTo) navigate(date, customTo)
    },
    [customTo, navigate]
  )

  const onCustomToChange = useCallback(
    (date: string) => {
      setCustomTo(date)
      if (customFrom && date) navigate(customFrom, date)
    },
    [customFrom, navigate]
  )

  const handleVisitorsPageChange = useCallback(
    (page: number) => {
      const { from, to } = computeDates(activeRange, customFrom, customTo)
      router.get('/admin/analytics', { from, to, visitors_page: page }, { preserveState: true, preserveScroll: true })
    },
    [activeRange, customFrom, customTo]
  )

  return {
    activeRange,
    customFrom,
    customTo,
    onRangeChange,
    onCustomFromChange,
    onCustomToChange,
    handleVisitorsPageChange,
  }
}
